// Pure, schema-driven resolution of rjsf field ids and `$data` relative
// references. This is the mechanism behind cascading reference dropdowns
// (see docs/superpowers/specs/2026-08-03-reference-select-widget-design.md
// and the upstream RFC in gundi-integration-cmore:
// docs/rfc-reference-data-portal-support.md).
//
// No UI here — see ReferenceSelectWidget for the widget that calls these.

export type Seg = string | number;

/** Follow a $ref chain (CMORE's pydantic schemas use "definitions"; pydantic
 * v2 / newer JSON Schema drafts use "$defs") until it resolves to a concrete
 * schema node. Returns null if the node/ref can't be resolved. */
function derefSchema(schema: any, rootSchema: any): any {
  let node = schema;
  const seen = new Set<string>();
  while (node && typeof node === "object" && typeof node.$ref === "string") {
    const refName = node.$ref.split("/").pop();
    if (!refName || seen.has(refName)) return null;
    seen.add(refName);
    const defs = rootSchema?.definitions ?? rootSchema?.$defs;
    node = defs ? defs[refName] : undefined;
  }
  return node ?? null;
}

/** Recursively match `remainingId` against `schemaNode`, consuming the
 * entire string. Property names may contain underscores, so an object
 * level tries each property key (longest first) as a candidate prefix and
 * backtracks (via the loop) if the recursive match on the remainder fails.
 * An array level only ever consumes a single leading numeric segment (per
 * the schema's node type — never a bare regex guess), then descends into
 * `items`. Returns null when the id can't be fully consumed. */
function matchNode(remainingId: string, schemaNode: any, rootSchema: any): Seg[] | null {
  const node = derefSchema(schemaNode, rootSchema);
  if (!node) return null;

  const isArray = node.type === "array" || (node.type === undefined && node.items !== undefined);
  const isObject = node.type === "object" || (node.type === undefined && node.properties !== undefined);

  if (isArray) {
    const match = /^\d+/.exec(remainingId);
    if (!match) return null;
    const index = parseInt(match[0], 10);
    const rest = remainingId.slice(match[0].length);
    if (rest === "") return [index];
    if (rest[0] !== "_") return null;
    const childPath = matchNode(rest.slice(1), node.items, rootSchema);
    return childPath === null ? null : [index, ...childPath];
  }

  if (isObject) {
    const properties = node.properties ?? {};
    const keys = Object.keys(properties).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      if (remainingId === key) return [key];
      if (remainingId.startsWith(`${key}_`)) {
        const childPath = matchNode(remainingId.slice(key.length + 1), properties[key], rootSchema);
        if (childPath !== null) return [key, ...childPath];
      }
    }
    return null;
  }

  return null;
}

/** Convert an rjsf field id (e.g. "root_event_type_to_tag_0_field_mappings_2_cmore_field_name")
 * into a data path (["event_type_to_tag", 0, "field_mappings", 2, "cmore_field_name"]).
 * Property names may contain underscores, so segments are matched against the
 * JSON schema: at an object level try each property name that prefixes the
 * remaining id (longest match first); at an array level consume one numeric
 * segment and descend into `items`. Returns null when the id can't be matched.
 *
 * `idPrefix` is rjsf's own: "root" unless the form was given one. Screens that
 * mount several forms at once namespace it per action, and an id that doesn't
 * match the root resolves to no path at all — which leaves a cascading dropdown
 * waiting forever on a parameter it cannot resolve. */
export function idToDataPath(id: string, rootSchema: any, idPrefix: string = "root"): Seg[] | null {
  const root = idPrefix || "root";
  if (id === root) return [];
  if (!id.startsWith(`${root}_`)) return null;
  const remainingId = id.slice(root.length + 1);
  return matchNode(remainingId, rootSchema, rootSchema);
}

/** Resolve a `$data` relative path from the field at `fieldPath`.
 * Start level = the object containing the field (drop the last segment).
 * Each "../" climbs one level, where array items and the array are distinct
 * levels (so from event_type_to_tag[0].field_mappings[1].cmore_field_name,
 * "../../tag_name" → event_type_to_tag[0].tag_name). The remainder after the
 * "../"s is a (possibly dotted) descendant path. Returns undefined when the
 * path walks out of the root or hits a missing value. */
export function resolveDataRef(relPath: string, fieldPath: Seg[], rootFormData: any): unknown {
  const upMatch = /^(?:\.\.\/)*/.exec(relPath);
  const upPrefix = upMatch ? upMatch[0] : "";
  const climbs = upPrefix.length / 3;
  const remainder = relPath.slice(upPrefix.length);

  const levelLength = fieldPath.length - 1 - climbs;
  if (levelLength < 0) return undefined;

  const levelPath = fieldPath.slice(0, levelLength);
  const remainderSegments = remainder === "" ? [] : remainder.split(".");
  const fullPath: Seg[] = [...levelPath, ...remainderSegments];

  let current: any = rootFormData;
  for (const seg of fullPath) {
    if (current === null || current === undefined) return undefined;
    current = current[seg as any];
  }
  return current;
}
